import { useParams } from 'react-router-dom';
import { useShareableAuthLink } from '../hooks/useSource';
import { usePageBranding } from '../hooks/usePageBranding';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertTriangle } from 'lucide-react';
import pliableLogo from '../assets/images/pliable/logo_with_text_black.png';

const DEFAULT_TITLE = 'Pliable';

export default function AuthShareLandingPage() {
  const { code } = useParams<{ code: string }>();
  const { data: details, isLoading, error } = useShareableAuthLink(code);

  const logoPath = details?.branding?.logo_path || pliableLogo;
  const brandName = details?.branding?.page_title || DEFAULT_TITLE;

  usePageBranding(details?.branding);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Link Unavailable</h2>
            <p className="text-sm text-muted-foreground">
              This link may have expired or is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-3">
          <img src={logoPath} alt={brandName} className="h-8 mx-auto" />
          {details.icon_path && (
            <img src={details.icon_path} alt="" className="h-12 w-12 mx-auto" />
          )}
          <CardTitle className="text-xl">
            Connect {details.connector_title}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Authorize your {details.connector_title} account so {brandName} can
            sync your data securely.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            className="w-full"
            onClick={() => {
              window.location.href = details.redirect_url;
            }}
          >
            Authorize {details.connector_title}
          </Button>
          <p className="text-xs text-center text-muted-foreground pt-2">
            You'll be redirected to {details.connector_title} to sign in. Powered
            by {brandName}.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
